import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { OptimizedImage } from "@/components/ui/optimized-image"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { imageSrcSet } from "@/data/location-images"
import { cdnUrl } from "@/lib/cdn"
import type { SeoMeta } from "@/types"
import { LOCATIONS } from "@/data/directory/registry"
import { getAreaGuide, getAreaGuidesForLocation } from "@/data/areas/registry"
import LiveDataSection, { type RawLiveData } from "@/components/public/live-data-section"

/* ------------------------------------------------------------------ */
/* Icons                                                               */
/* ------------------------------------------------------------------ */

function IconTrain({ className = "size-5" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className={className}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 0 1-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0H21M3.375 14.25h17.25" />
    </svg>
  )
}

function IconHome({ className = "size-5" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className={className}>
      <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
    </svg>
  )
}

function IconStar({ className = "size-5" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className={className}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456Z" />
    </svg>
  )
}

function IconCheck({ className = "size-4" }: { className?: string }) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className={className}>
      <path
        fillRule="evenodd"
        d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
        clipRule="evenodd"
      />
    </svg>
  )
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

export default function AreaShow({
  meta,
  location,
  area,
  liveData,
}: {
  meta: SeoMeta
  location: string
  area: string
  liveData: RawLiveData | null
}) {
  const locationData = LOCATIONS[location]
  const guide = getAreaGuide(location, area)

  if (!locationData || !guide) {
    return (
      <PublicLayout>
        <section className="px-6 py-24 text-center">
          <h1 className="font-display text-3xl font-bold text-charcoal">
            Area guide not found<span className="text-red">.</span>
          </h1>
          <p className="mt-4 text-slate">
            We couldn&rsquo;t find that area guide.{" "}
            <Link href="/areas" className="text-red hover:text-deep-red">
              Browse all area guides
            </Link>
            .
          </p>
        </section>
      </PublicLayout>
    )
  }

  const otherGuides = getAreaGuidesForLocation(location).filter(
    (g) => g.slug !== area
  )
  const nearbyGuides = otherGuides.slice(0, 3)

  // Find directory offices covering this area
  const localOffices = locationData.offices.filter((o) =>
    o.areas.some(
      (a) => a.toLowerCase() === guide.name.toLowerCase()
    )
  )

  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />

        {/* Structured Data — Place */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Place",
            name: `${guide.name}, ${locationData.name}`,
            description: meta.description,
            address: {
              "@type": "PostalAddress",
              addressLocality: guide.name,
              addressRegion: locationData.name,
              addressCountry: "GB",
            },
          })}
        </script>
        {/* Structured Data — BreadcrumbList */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${new URL(meta.canonical).origin}/` },
              { "@type": "ListItem", position: 2, name: "Area Guides", item: `${new URL(meta.canonical).origin}/areas` },
              { "@type": "ListItem", position: 3, name: locationData.name, item: `${new URL(meta.canonical).origin}/areas/${location}` },
              { "@type": "ListItem", position: 4, name: guide.name },
            ],
          })}
        </script>
      </Head>

      {/* Breadcrumb */}
      <nav className="border-b border-stone px-6 py-3">
        <div className="mx-auto max-w-6xl">
          <ol className="flex items-center gap-2 text-xs text-warm-grey">
            <li>
              <Link href="/areas" className="hover:text-charcoal">
                Area Guides
              </Link>
            </li>
            <li>/</li>
            <li>
              <Link
                href={`/areas/${location}`}
                className="hover:text-charcoal"
              >
                {locationData.name}
              </Link>
            </li>
            <li>/</li>
            <li className="font-medium text-charcoal">{guide.name}</li>
          </ol>
        </div>
      </nav>

      {/* Hero image */}
      {guide.imageUrl && (
        <div className="relative h-64 w-full overflow-hidden sm:h-80 md:h-96">
          <OptimizedImage
            eager
            src={cdnUrl(guide.imageUrl)}
            srcSet={imageSrcSet(guide.imageUrl)}
            sizes="100vw"
            alt={`${guide.name}, ${locationData.name}`}
            className="h-full w-full object-cover"
            width={1200}
            height={594}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
        </div>
      )}

      {/* Hero */}
      <section className="px-6 py-20 md:py-28">
        <div className="mx-auto max-w-6xl">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
            {locationData.name} Area Guide
          </p>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl md:text-6xl">
            {guide.name}, {locationData.name}<span className="text-red">.</span>
          </h1>
          <p className="mt-4 text-xl italic text-slate">{guide.tagline}</p>

          {/* Quick facts */}
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
            <div className="rounded-lg border border-stone bg-cream/50 p-4">
              <p className="text-[10px] font-medium uppercase tracking-[0.15em] text-warm-grey">
                Avg. price
              </p>
              <p className="mt-1 font-display text-xl font-bold text-charcoal">
                {guide.avgPrice}
              </p>
            </div>
            <div className="rounded-lg border border-stone bg-cream/50 p-4">
              <p className="text-[10px] font-medium uppercase tracking-[0.15em] text-warm-grey">
                Price range
              </p>
              <p className="mt-1 font-display text-sm font-bold text-charcoal">
                {guide.priceRange}
              </p>
            </div>
            <div className="rounded-lg border border-stone bg-cream/50 p-4">
              <p className="text-[10px] font-medium uppercase tracking-[0.15em] text-warm-grey">
                Best for
              </p>
              <div className="mt-1 flex flex-wrap gap-1">
                {guide.bestFor.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full bg-red/8 px-2 py-0.5 text-[10px] font-medium text-red"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
            <div className="rounded-lg border border-stone bg-cream/50 p-4">
              <p className="text-[10px] font-medium uppercase tracking-[0.15em] text-warm-grey">
                Local agents
              </p>
              <p className="mt-1 font-display text-xl font-bold text-charcoal">
                {localOffices.length}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            Overview<span className="text-red">.</span>
          </h2>
          <div className="mt-6 space-y-4 text-base leading-relaxed text-slate">
            {guide.description.split("\n\n").map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        </div>
      </section>

      {/* Property types */}
      <section className="border-t border-stone bg-cream px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal>
            <div className="flex items-center gap-3">
              <div className="flex size-10 items-center justify-center rounded-full bg-red/10">
                <IconHome className="size-5 text-red" />
              </div>
              <h2 className="font-display text-2xl font-bold text-charcoal">
                Property types<span className="text-red">.</span>
              </h2>
            </div>
            <div className="mt-6 flex flex-wrap gap-2">
              {guide.propertyTypes.map((type) => (
                <span
                  key={type}
                  className="rounded-lg border border-stone bg-white px-4 py-2 text-sm font-medium text-charcoal"
                >
                  {type}
                </span>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Transport */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal>
            <div className="flex items-center gap-3">
              <div className="flex size-10 items-center justify-center rounded-full bg-red/10">
                <IconTrain className="size-5 text-red" />
              </div>
              <h2 className="font-display text-2xl font-bold text-charcoal">
                Transport<span className="text-red">.</span>
              </h2>
            </div>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {guide.transport.nearestStation && (
                <div className="rounded-lg border border-stone bg-white p-4">
                  <p className="text-xs font-medium text-warm-grey">Nearest station</p>
                  <p className="mt-1 text-sm font-medium text-charcoal">
                    {guide.transport.nearestStation}
                  </p>
                </div>
              )}
              {guide.transport.commuteTime && (
                <div className="rounded-lg border border-stone bg-white p-4">
                  <p className="text-xs font-medium text-warm-grey">Commute</p>
                  <p className="mt-1 text-sm font-medium text-charcoal">
                    {guide.transport.commuteTime}
                  </p>
                </div>
              )}
              {guide.transport.busRoutes && (
                <div className="rounded-lg border border-stone bg-white p-4">
                  <p className="text-xs font-medium text-warm-grey">Bus routes</p>
                  <p className="mt-1 text-sm font-medium text-charcoal">
                    {guide.transport.busRoutes}
                  </p>
                </div>
              )}
              {guide.transport.parking && (
                <div className="rounded-lg border border-stone bg-white p-4">
                  <p className="text-xs font-medium text-warm-grey">Parking</p>
                  <p className="mt-1 text-sm font-medium text-charcoal">
                    {guide.transport.parking}
                  </p>
                </div>
              )}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Lifestyle */}
      <section className="border-t border-stone bg-cream px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal>
            <div className="flex items-center gap-3">
              <div className="flex size-10 items-center justify-center rounded-full bg-red/10">
                <IconStar className="size-5 text-red" />
              </div>
              <h2 className="font-display text-2xl font-bold text-charcoal">
                Lifestyle<span className="text-red">.</span>
              </h2>
            </div>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {[
                { label: "Restaurants & cafes", value: guide.lifestyle.restaurants },
                { label: "Schools", value: guide.lifestyle.schools },
                { label: "Green spaces", value: guide.lifestyle.greenSpaces },
                { label: "Shopping", value: guide.lifestyle.shopping },
              ].map((item) => (
                <div key={item.label} className="rounded-lg border border-stone bg-white p-4">
                  <p className="text-xs font-medium text-warm-grey">{item.label}</p>
                  <p className="mt-1 text-sm leading-relaxed text-charcoal">
                    {item.value}
                  </p>
                </div>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Highlights */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            Highlights<span className="text-red">.</span>
          </h2>
          <ul className="mt-6 space-y-3">
            {guide.highlights.map((h) => (
              <li key={h} className="flex items-start gap-3">
                <IconCheck className="mt-0.5 size-4 shrink-0 text-red" />
                <span className="text-base text-slate">{h}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Local agents */}
      {localOffices.length > 0 && (
        <section className="border-t border-stone bg-white px-6 py-16">
          <div className="mx-auto max-w-3xl">
            <h2 className="font-display text-2xl font-bold text-charcoal">
              Estate agents in {guide.name}<span className="text-red">.</span>
            </h2>
            <p className="mt-2 text-sm text-slate">
              {localOffices.length} agent{localOffices.length === 1 ? "" : "s"}{" "}
              covering {guide.name} in our directory.
            </p>
            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              {localOffices.map((office) => (
                <Link
                  key={office.slug}
                  href={`/directory/${location}/${office.slug}`}
                  className="flex items-center gap-3 rounded-lg border border-stone bg-cream/50 p-4 transition-all hover:border-stone/80 hover:shadow-sm"
                >
                  <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-stone text-xs font-semibold text-charcoal">
                    {office.name
                      .split(" ")
                      .slice(0, 2)
                      .map((w) => w[0])
                      .join("")
                      .toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-charcoal">
                      {office.name}
                    </p>
                    <p className="text-xs text-warm-grey">{office.branch}</p>
                  </div>
                  <div className="ml-auto flex items-center gap-1">
                    <svg viewBox="0 0 20 20" fill="#C41E2A" className="size-3.5">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 0 0 .95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 0 0-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 0 0-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 0 0-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 0 0 .951-.69l1.07-3.292Z" />
                    </svg>
                    <span className="text-xs font-medium text-charcoal">
                      {office.rating.toFixed(1)}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
            <div className="mt-4">
              <Link
                href={`/directory/${location}`}
                className="text-sm font-medium text-red hover:text-deep-red"
              >
                See all {locationData.name} agents &rarr;
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Neighbouring areas */}
      {nearbyGuides.length > 0 && (
        <section className="border-t border-stone bg-cream px-6 py-16">
          <div className="mx-auto max-w-3xl">
            <h2 className="font-display text-2xl font-bold text-charcoal">
              Other {locationData.name} areas<span className="text-red">.</span>
            </h2>
            <div className="mt-6 grid gap-3 sm:grid-cols-3">
              {nearbyGuides.map((g) => (
                <Link
                  key={g.slug}
                  href={`/areas/${location}/${g.slug}`}
                  className="group overflow-hidden rounded-lg border border-stone bg-white transition-all hover:border-stone/80 hover:shadow-sm"
                >
                  {g.imageUrl && (
                    <div className="h-32 overflow-hidden">
                      <OptimizedImage
                        src={cdnUrl(g.imageUrl)}
                        srcSet={imageSrcSet(g.imageUrl)}
                        sizes="(max-width: 640px) 100vw, 33vw"
                        alt={g.name}
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                        width={800}
                        height={396}
                      />
                    </div>
                  )}
                  <div className="p-4">
                    <p className="font-display text-base font-bold text-charcoal">
                      {g.name}
                    </p>
                    <p className="mt-0.5 text-xs italic text-slate">{g.tagline}</p>
                    <p className="mt-2 text-sm font-semibold text-charcoal">
                      {g.avgPrice}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
            <div className="mt-4">
              <Link
                href={`/areas/${location}`}
                className="text-sm font-medium text-red hover:text-deep-red"
              >
                See all {locationData.name} area guides &rarr;
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Live data */}
      <LiveDataSection data={liveData} />

      {/* CTA */}
      <section className="border-t border-stone bg-charcoal px-6 py-20">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold text-cream md:text-4xl">
            Selling in {guide.name}<span className="text-red">?</span>
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base text-warm-grey">
            Make your listing stand out with a scroll-stopping video ad. Paste a
            listing URL, pick a template, and get a professional video in under a
            minute.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              href="/demo"
              className="inline-flex w-full items-center justify-center rounded-md bg-red px-6 py-3 text-base font-medium text-white transition-colors hover:bg-deep-red sm:w-auto"
            >
              Request a demo
            </Link>
            <Link
              href="/how-it-works"
              className="inline-flex w-full items-center justify-center rounded-md border border-slate bg-charcoal px-6 py-3 text-base font-medium text-cream transition-colors hover:bg-slate sm:w-auto"
            >
              See how it works
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  )
}
